// Preset animations — whole-body clips played directly, bypassing the
// conversational graph, so a new clip can be eyeballed the moment it lands in a
// bundle. Two playback paths, checked in order (each owns its own mesh, so the
// first one present is the only one that could work):
//
//   1. mesh-only OGS preview (__OGS_MESH_PREVIEW__) — a {rig.glb, <clip>.glb}
//      bundle with no inference assets; owns its own mixer.
//   2. the live body — the clip takes the __DIRECT_PLAY__ exclusive override.

// Curated list. Add a name here once its clip ships in a character's animation
// assets — availability is resolved against the live runtime, never assumed.
// Empty: its one entry was macarena, a bundle-only test clip no character ships.
export const PRESET_ANIMATIONS = []

function win_(win) {
  if (win) return win
  return typeof window !== 'undefined' ? window : undefined
}

/** True when `name` is loaded on whichever mesh path is mounted. */
export function isPresetAvailable(name, win) {
  const w = win_(win)
  if (!w || !name) return false
  const preview = w.__OGS_MESH_PREVIEW__
  if (preview) return !!preview.clipNames?.().includes(name)
  return !!w.__AVATAR_ACTIONS__?.[name]
}

/** True when `name` is currently playing on any path. */
export function isPresetPlaying(name, win) {
  const w = win_(win)
  if (!w || !name) return false
  return w.__OGS_MESH_PREVIEW__?.playing === name
    || w.__DIRECT_PLAY__?.clipName === name
}

/** Toggle `name` on whichever mesh is active. Returns the resulting state:
 *  'preview-play' | 'preview-stop' | 'direct-play' | 'direct-stop' | 'missing'. */
export function togglePreset(name, win) {
  const w = win_(win)
  if (!w || !name) return 'missing'

  const preview = w.__OGS_MESH_PREVIEW__
  if (preview) {
    if (preview.playing === name) { preview.stop(); return 'preview-stop' }
    if (!preview.clipNames?.().includes(name)) {
      console.warn(`[preset-anim] '${name}' is not in this OGS bundle`)
      return 'missing'
    }
    preview.play(name)
    return 'preview-play'
  }

  // Releasing the override lets the blueprint graph drive the rig again.
  if (w.__DIRECT_PLAY__?.clipName === name) { w.__DIRECT_PLAY__ = null; return 'direct-stop' }

  const action = w.__AVATAR_ACTIONS__?.[name]
  if (!action) {
    console.warn(`[preset-anim] no '${name}' clip loaded for this character`)
    return 'missing'
  }
  w.__DIRECT_PLAY__ = { clipName: name, expiresAt: Date.now() + action.getClip().duration * 1000 }
  return 'direct-play'
}
